import { BrgEvent, CalendarDay } from './types';

/**
 * Generate calendar days for the given month
 * Only includes weekdays (Monday-Friday), excludes Saturday-Sunday
 * Maintains proper grid positioning for 5-column layout
 */
export function generateCalendarDays(date: Date, events: BrgEvent[]): CalendarDay[] {
  const year = date.getFullYear();
  const month = date.getMonth();

  const days: CalendarDay[] = [];
  
  // Start from the first day of the month
  const currentDate = new Date(year, month, 1);
  
  // Start from the Monday of the week containing the first day of the month
  // If first day is Friday (5), go back to Monday of that week
  const firstDayOfWeek = currentDate.getDay(); // 0=Sunday, 1=Monday, etc.
  const daysToGoBack = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Go back to Monday
  
  const startDate = new Date(year, month, 1 - daysToGoBack);
  
  // Generate 25 weeks worth of days (enough to cover any month)
  // But only add weekdays (Monday-Friday)
  let dayNum = 0;
  while (days.length < 25) { // Limit to 25 weekdays (5 weeks * 5 days)
    const loopDate = new Date(startDate);
    loopDate.setDate(loopDate.getDate() + dayNum);
    
    const dayOfWeek = loopDate.getDay();
    
    // Only add weekdays (Monday=1 to Friday=5)
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      const dateStr = formatDate(loopDate);
      const dateNum = loopDate.getDate();
      const isCurrentMonth = loopDate.getMonth() === month;
      
      days.push({
        date: dateNum,
        isCurrentMonth: isCurrentMonth,
        events: getEventsForDate(dateStr, events),
        fullDate: dateStr,
      });
    }
    
    dayNum++;
  }

  return days;
}

/**
 * Get all events for a specific date
 */
export function getEventsForDate(dateStr: string, events: BrgEvent[]): BrgEvent[] {
  return events.filter(event => event.date === dateStr);
}

/**
 * Format date to YYYY-MM-DD string
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Sort events by time, then alphabetically by title
 */
export function sortEventsByTimeAndTitle(a: BrgEvent, b: BrgEvent): number {
  // Compare by time
  if (a.time !== b.time) {
    return a.time.localeCompare(b.time);
  }
  // If same time, compare by title
  return a.eventName.localeCompare(b.eventName);
}

/**
 * Format time from HH:MM to readable format
 */
export function formatTime(time: string): string {
  try {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const minute = minutes;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute} ${ampm}`;
  } catch {
    return time;
  }
}

/**
 * Format date to display format (e.g., "April 15, 2025 • 1:00 PM")
 */
export function formatDateAndTime(dateStr: string, timeStr: string): string {
  try {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedTime = formatTime(timeStr);
    return `${formattedDate} • ${formattedTime}`;
  } catch {
    return `${dateStr} • ${timeStr}`;
  }
}

/**
 * Truncate text to specified length
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
}

/**
 * Parse date string to Date object
 */
export function parseDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
}
